import { Box, IconButton, ListItem } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Color, ColorDynamic } from '@superdispatch/ui';
import { TextBox } from '@superdispatch/ui-lab';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { trackSuperCardEvent } from 'shared/helpers/Analytics';
import { useSuperCard } from './data/SuperCardAPI';
import { useSuperCardBannerDismiss } from './data/useSuperCardBannerDismiss';

const BannerContainer = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  border-radius: 8px;
  background-color: ${ColorDynamic.Blue300};
`;

const BannerBody = styled.button`
  display: flex;
  flex: 1;
  align-items: flex-end;
  padding-right: 10px;
  min-width: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;

  &:focus-visible {
    outline: 2px solid ${ColorDynamic.Blue500};
    outline-offset: -2px;
  }
`;

const BannerImage = styled.img`
  height: 96px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const DismissButton = styled(IconButton)`
  position: absolute;
  top: 4px;
  right: 4px;
  padding: 4px;
  color: ${Color.White};
`;

const BannerText = styled(TextBox)`
  color: ${Color.White};
`;

interface SuperCardApplicationBannerProps {
  placement: string;
  as?: 'default' | 'list-item';
}

const BANNER_COPY =
  'Give your drivers a fuel card that saves up to $2/gal. No hidden fees. Integrated to driver app.';

const BANNER_IMAGE_URL =
  'https://storage.googleapis.com/superdispatch-email-resources/super-card/banner.png';

const BANNER_VISIBLE_STATUSES = ['PREQUALIFIED', 'IN_PROGRESS'];

const BANNER_CLICK_EVENT = 'CTMS: Clicked SuperCard Application Banner';

export function SuperCardApplicationBanner({
  placement,
  as = 'default',
}: SuperCardApplicationBannerProps) {
  const navigate = useNavigate();
  const { data: superCard, isLoading, isError } = useSuperCard();
  const { isDismissed, dismiss } = useSuperCardBannerDismiss(placement);

  if (isLoading || isError || !superCard) {
    return null;
  }

  if (!superCard.has_view_access) {
    return null;
  }

  if (!BANNER_VISIBLE_STATUSES.includes(superCard?.status || '')) {
    return null;
  }

  if (isDismissed) {
    return null;
  }

  const BannerContent = (
    <BannerContainer>
      <BannerBody
        type="button"
        onClick={() => {
          trackSuperCardEvent(BANNER_CLICK_EVENT, { placement });
          navigate('/super-card');
        }}
      >
        <Box padding="15px 8px">
          <BannerText variant="body-semibold" color="white">
            {BANNER_COPY}
          </BannerText>
        </Box>
        <BannerImage alt="" aria-hidden={true} src={BANNER_IMAGE_URL} />
      </BannerBody>
      <DismissButton aria-label="Dismiss SuperCard banner" onClick={dismiss}>
        <Close fontSize="medium" />
      </DismissButton>
    </BannerContainer>
  );

  if (as === 'list-item') {
    return (
      <ListItem key="assign-driver-drawer-banner">{BannerContent}</ListItem>
    );
  }

  return BannerContent;
}
